import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import BadgeVerificado from './BadgeVerificado';
import { lugarDe, PROVINCIA_DEFECTO } from '../lib/ubicacion';
import '../styles/PerfilCompleto.css';

// '3' → '3 hijos/as'; 'No tengo', 'Más de 10', etc. se muestran tal cual
export const hijosLabel = (v) => /^\d+$/.test(v) ? `${v} ${v === '1' ? 'hijo/a' : 'hijos/as'}` : v === 'Más de 10' ? 'Más de 10 hijos/as' : v === 'No tengo' ? 'Sin hijos' : v;

// "Prefiero no decir" y los vacíos no se muestran
const valor = (v) => (v === null || v === undefined || v === '' || v === 'Prefiero no decir') ? null : String(v);

function Dato({ icono, label, v }) {
  if (!valor(v)) return null;
  return (
    <div className="perfil-dato">
      <span className="perfil-dato-label">{icono ? `${icono} ${label}` : label}</span>
      <span className="perfil-dato-valor">{v}</span>
    </div>
  );
}

function Seccion({ titulo, children }) {
  // Una sección sin ningún dato completado no se muestra
  const conDato = React.Children.toArray(children).some(c => c && valor(c.props.v));
  if (!conDato) return null;
  return (
    <div className="perfil-seccion">
      <h3 className="perfil-seccion-titulo">{titulo}</h3>
      <div className="perfil-datos">{children}</div>
    </div>
  );
}

// Perfil completo de una persona: todas las fotos y todo lo que completó.
// `profile` puede venir incompleto (ej. desde el chat); con `cargarId` se piden los datos al servidor.
// Nunca muestra teléfono ni WhatsApp (el servidor tampoco los manda).
export default function PerfilCompleto({ profile: inicial, cargarId, onClose, acciones }) {
  const [profile, setProfile] = useState(inicial || null);
  const [error, setError] = useState('');
  const [foto, setFoto] = useState(0);

  useEffect(() => {
    if (!cargarId) return;
    api.getProfile(cargarId)
      .then(p => setProfile(prev => ({ ...(prev || {}), ...p })))
      .catch(e => setError(e.message || 'No se pudo cargar el perfil.'));
  }, [cargarId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = overflow; };
  }, [onClose]);

  const p = profile || {};
  const fotos = Array.isArray(p.fotos) && p.fotos.length ? p.fotos : (p.foto_path ? [p.foto_path] : []);
  const actual = Math.min(foto, Math.max(fotos.length - 1, 0));
  const mover = (d) => setFoto((actual + d + fotos.length) % fotos.length);
  const lista = (v) => (Array.isArray(v) ? v : []).filter(x => valor(x));

  return (
    <div className="modal-overlay perfil-completo-overlay" onClick={onClose}>
      <div className="perfil-completo" role="dialog" aria-modal="true" aria-label={`Perfil de ${p.name || ''}`} onClick={e => e.stopPropagation()}>
        <button className="perfil-completo-cerrar" onClick={onClose} aria-label="Cerrar">✕</button>

        <div className="perfil-completo-foto">
          {fotos.length
            ? <img src={fotos[actual]} alt={`${p.name || ''} — foto ${actual + 1}`} />
            : <div className="profile-card-avatar">{(p.name || 'U')[0].toUpperCase()}</div>}
          {fotos.length > 1 && (
            <>
              <div className="perfil-completo-puntos">
                {fotos.map((f, i) => <span key={f + i} className={i === actual ? 'activo' : ''} />)}
              </div>
              <button className="perfil-completo-flecha izq" onClick={() => mover(-1)} aria-label="Foto anterior">‹</button>
              <button className="perfil-completo-flecha der" onClick={() => mover(1)} aria-label="Foto siguiente">›</button>
            </>
          )}
          {p.nota && <div className="nota-burbuja nota-burbuja--card">{p.nota}</div>}
          <div className="profile-card-overlay">
            {p.vip && <span className="vip-badge">👑 Usuario VIP</span>}
            {p.afinidad != null && <span className="afinidad-badge">💞 {p.afinidad}% afinidad</span>}
            <div className="profile-card-name">{p.name}{p.age ? `, ${p.age}` : ''}<BadgeVerificado show={p.verificado} dorado={p.verif_dorado} /></div>
            {p.departamento && <div className="profile-card-dept">📍 {lugarDe(p)}</div>}
          </div>
        </div>

        {fotos.length > 1 && (
          <div className="perfil-completo-miniaturas">
            {fotos.map((f, i) => (
              <button key={f + i} className={i === actual ? 'activa' : ''} onClick={() => setFoto(i)} aria-label={`Ver foto ${i + 1}`}>
                <img src={f} alt="" />
              </button>
            ))}
          </div>
        )}

        <div className="perfil-completo-cuerpo">
          {!profile && !error && <p className="perfil-completo-cargando">Cargando perfil…</p>}
          {error && <p className="perfil-completo-cargando">{error}</p>}

          {p.bio && (
            <div className="perfil-seccion">
              <h3 className="perfil-seccion-titulo">Sobre mí</h3>
              <p className="perfil-completo-bio">{p.bio}</p>
            </div>
          )}

          <Seccion titulo="Lo básico">
            <Dato icono="🗺️" label="Provincia" v={p.departamento ? (p.provincia || PROVINCIA_DEFECTO) : null} />
            <Dato icono="📍" label="Departamento" v={valor(p.departamento)} />
            <Dato icono="⚧" label="Género" v={valor(p.genero)} />
            <Dato icono="" label="Orientación" v={valor(p.orientacion)} />
            <Dato icono="👀" label="Le interesan" v={valor(p.buscando)} />
            <Dato icono="💞" label="Busca" v={lista(p.busca_relacion).join(', ')} />
            <Dato icono="💍" label="Estado civil" v={valor(p.estado_civil)} />
          </Seccion>

          <Seccion titulo="Físico">
            <Dato icono="📏" label="Altura" v={p.altura ? `${p.altura} cm` : null} />
            <Dato icono="💪" label="Contextura" v={valor(p.contextura)} />
            <Dato icono="💇" label="Pelo" v={valor(p.color_pelo)} />
            <Dato icono="👁️" label="Ojos" v={valor(p.color_ojos)} />
          </Seccion>

          {lista(p.personalidad).length > 0 && (
            <div className="perfil-seccion">
              <h3 className="perfil-seccion-titulo">Personalidad</h3>
              <div className="profile-card-meta">
                {lista(p.personalidad).map(t => <span key={t} className="meta-chip">✨ {t}</span>)}
              </div>
            </div>
          )}

          <Seccion titulo="Estilo de vida">
            <Dato icono="🎓" label="Educación" v={valor(p.educacion)} />
            <Dato icono="✨" label="Estilo de vida" v={valor(p.estilo_vida)} />
            <Dato icono="🚬" label="Tabaco" v={valor(p.habitos_tabaco)} />
            <Dato icono="🍷" label="Alcohol" v={valor(p.habitos_alcohol)} />
            <Dato icono="👶" label="Hijos" v={valor(p.tiene_hijos) && hijosLabel(p.tiene_hijos)} />
            <Dato icono="🍼" label="Quiere hijos" v={valor(p.quiere_hijos)} />
            <Dato icono="🐾" label="Mascotas" v={p.mascota === 'No tengo' ? 'Sin mascotas' : valor(p.mascota)} />
            <Dato icono="🙏" label="Religión" v={valor(p.religion)} />
          </Seccion>

          {lista(p.intereses).length > 0 && (
            <div className="perfil-seccion">
              <h3 className="perfil-seccion-titulo">Intereses</h3>
              <div className="profile-card-interests">
                {lista(p.intereses).map(i => <span key={i} className="interest-chip">{i}</span>)}
              </div>
            </div>
          )}
        </div>

        {acciones && <div className="perfil-completo-acciones">{acciones}</div>}
      </div>
    </div>
  );
}
